"use client";

import { createContext, useState, useEffect, useRef, ReactNode, useMemo, useCallback } from "react";
import { calcBundle, type BundleResult } from "@/lib/promotions";
import { EXPRESS_SHIPPING_EXTRA, FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING_FEE } from "@/lib/orders";
import { newGuestKey } from "@/lib/carts";
import { apiGet, apiPut } from "@/lib/api-client";
import { useAuth } from "@/hooks/useAuth";

export type CartItem = {
  slug: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  /** Varyantlı ürünlerde satır kimliğinin parçası (örn. "Renk 01"). */
  variant?: string;
};

/** Sepet satır anahtarı — aynı ürünün farklı varyantları ayrı satırdır. */
export function cartLineKey(item: Pick<CartItem, "slug" | "variant">): string {
  return `${item.slug}::${item.variant ?? ""}`;
}

type CartContextType = {
  items: CartItem[];
  total: number;
  discount: number;
  /** 4 Al 3 Öde otomatik indirimi */
  promoDiscount: number;
  bundle: BundleResult;
  coupon: string | null;
  totalAfterDiscount: number;
  /** Kargo ücreti (ayar yoksa kod sabiti) */
  shipping: number;
  freeShip: boolean;
  /** Kargo politikası (admin ayarından, yoksa kod sabiti) */
  freeShipThreshold: number;
  standardFee: number;
  /** Kargo dahil ödenecek tutar — checkout'un gönderdiği tutarla aynı formül */
  grandTotal: number;
  count: number;
  addItem: (item: CartItem) => void;
  removeItem: (slug: string, variant?: string) => void;
  updateQuantity: (slug: string, quantity: number, variant?: string) => void;
  clear: () => void;
  applyCoupon: (code: string) => Promise<{ ok: boolean; message: string }>;
  removeCoupon: () => void;
};

export const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [coupon, setCoupon] = useState<string | null>(null);
  // Server-doğrulamalı kupon oranı + alt limit (checkout ile aynı kaynak)
  const [couponRate, setCouponRate] = useState(0);
  const [couponMin, setCouponMin] = useState(0);
  const validatedCoupon = useRef<string | null>(null);
  const [shipCfg, setShipCfg] = useState<{ threshold: number; fee: number; expressExtra: number } | null>(null);
  const initialized = useRef(false);
  const restored = useRef(false);
  const everSynced = useRef(false);
  const { user, isLoading: authLoading } = useAuth();
  const userEmail = user?.email?.trim().toLowerCase() || null;

  // Misafir kimliği — localStorage'da sabit, sekme yenilemelerinde aynı kalır
  const [guestId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      let g = localStorage.getItem("onomac-guest-id");
      if (!g) {
        g = newGuestKey();
        localStorage.setItem("onomac-guest-id", g);
      }
      return g;
    } catch {
      return null;
    }
  });

  // Vitrin ara toplamı (kupon öncesi, promo sonrası) — server validasyonuna girdi
  const promoSubtotal = useMemo(() => {
    const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const bundle = calcBundle(items.map((i) => ({ slug: i.slug, quantity: i.quantity })));
    return Math.round((total - bundle.discount) * 100) / 100;
  }, [items]);

  /** Kuponu server'a doğrulat (süre/limit/min-sepet checkout ile aynı kurallar). */
  const validateCouponOnServer = useCallback(async (code: string, subtotal: number) => {
    const res = await apiGet<{ rate?: unknown; minSubtotal?: unknown; error?: unknown }>(
      `/api/coupons?code=${encodeURIComponent(code)}&subtotal=${subtotal}`,
      "cart-coupon-validate"
    );
    if (!res.ok) return { ok: false as const, error: res.error };
    const rate = typeof res.data.rate === "number" ? res.data.rate : 0;
    if (!(rate > 0)) return { ok: false as const, error: "Geçersiz kod" };
    const min = typeof res.data.minSubtotal === "number" ? res.data.minSubtotal : 0;
    return { ok: true as const, rate, minSubtotal: min };
  }, []);

  // Kargo politikası admin ayarından (yoksa kod sabiti)
  useEffect(() => {
    let cancelled = false;
    apiGet<{ settings?: Record<string, string> }>("/api/settings", "cart-settings").then((res) => {
      if (cancelled || !res.ok) return;
      const t = Number(String(res.data.settings?.free_shipping_threshold ?? "").replace(",", "."));
      const f = Number(String(res.data.settings?.standard_shipping_fee ?? "").replace(",", "."));
      const x = Number(String(res.data.settings?.express_shipping_extra ?? "").replace(",", "."));
      if (t > 0 && f >= 0) {
        setShipCfg({ threshold: t, fee: f, expressExtra: x > 0 ? x : EXPRESS_SHIPPING_EXTRA });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // İlk yükleme: localStorage oku (ezme yok). Kupon iyimser yüklenir,
  // aşağıdaki sessiz doğrulama etkisi geçersizse düşürür.
  useEffect(() => {
    if (initialized.current) return;
    try {
      const raw = localStorage.getItem("onomac-cart");
      const couponRaw = localStorage.getItem("onomac-coupon");
      if (raw) {
        const parsed = JSON.parse(raw) as CartItem[];
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (Array.isArray(parsed)) setItems(parsed.filter((i) => i.slug && i.quantity > 0));
      }
      if (couponRaw) {
        setCoupon(couponRaw.trim().toUpperCase());
      }
    } catch {}
    initialized.current = true;
  }, []);

  // Sadece init sonrası yaz (mount ezme race fix)
  useEffect(() => {
    if (!initialized.current) return;
    try {
      localStorage.setItem("onomac-cart", JSON.stringify(items));
      // Terk edilmiş sepet takibi: son sepet hareketi
      if (items.length > 0) localStorage.setItem("onomac-cart-activity", String(Date.now()));
      else localStorage.removeItem("onomac-cart-activity");
    } catch {}
  }, [items]);

  useEffect(() => {
    if (!initialized.current) return;
    try {
      if (coupon) localStorage.setItem("onomac-coupon", coupon);
      else localStorage.removeItem("onomac-coupon");
    } catch {}
  }, [coupon]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "onomac-cart" && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as CartItem[];
          if (Array.isArray(parsed)) setItems(parsed);
        } catch {}
      }
      if (e.key === "onomac-coupon") {
        validatedCoupon.current = null;
        setCouponRate(0);
        setCouponMin(0);
        setCoupon(e.newValue ? e.newValue.trim().toUpperCase() : null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Server'dan geri yükle (cihazlar arası devam) — yalnızca yerel sepet boşsa.
  // Auth çözülmeden çalışmaz: girişlide e-posta sepeti, misafirde guest sepeti okunur.
  useEffect(() => {
    if (!initialized.current || restored.current || authLoading) return;
    restored.current = true;
    if (items.length > 0) return;
    const q = userEmail ? "" : `?ownerKey=${encodeURIComponent(guestId ?? "")}`;
    apiGet<{ cart?: { items?: unknown; coupon?: unknown } }>(`/api/cart${q}`, "cart-restore").then((res) => {
      if (!res.ok) return;
      const rows = Array.isArray(res.data.cart?.items) ? (res.data.cart.items as CartItem[]) : [];
      const valid = rows.filter((i) => i && typeof i.slug === "string" && i.quantity > 0);
      if (valid.length > 0) {
        setItems(valid.map((i) => ({ slug: i.slug, name: String(i.name ?? i.slug), price: Number(i.price) || 0, quantity: Math.min(99, Math.floor(i.quantity) || 1), ...(i.image ? { image: String(i.image) } : {}), ...(typeof i.variant === "string" && i.variant ? { variant: i.variant.slice(0, 40) } : {}) })));
        if (typeof res.data.cart?.coupon === "string" && res.data.cart.coupon.trim()) {
          validatedCoupon.current = null;
          setCouponRate(0);
          setCouponMin(0);
          setCoupon(res.data.cart.coupon.trim().toUpperCase());
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, authLoading]);

  // Sessiz kupon doğrulama — geri yüklenen kupon server'da doğrulanır,
  // geçersizse (süre/limit dolmuş) sessizce düşer. Alt-limit düşüşleri ayrı
  // etkide izlenir; checkout nihai otoritedir.
  // NOT: promoSubtotal bilerek deps'te yok (her adet değişiminde API vurmamak için).
  useEffect(() => {
    if (!coupon || validatedCoupon.current === coupon) return;
    let cancelled = false;
    validateCouponOnServer(coupon, promoSubtotal).then((v) => {
      if (cancelled) return;
      if (v.ok) {
        validatedCoupon.current = coupon;
        setCouponRate(v.rate);
        setCouponMin(v.minSubtotal);
      } else {
        validatedCoupon.current = null;
        setCouponRate(0);
        setCouponMin(0);
        setCoupon(null);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, validateCouponOnServer]);

  // Sepet alt limite düşünce kuponu vitrinde düşür (checkout zaten reddeder)
  useEffect(() => {
    if (coupon && validatedCoupon.current === coupon && couponMin > 0 && promoSubtotal < couponMin) {
      validatedCoupon.current = null;
      setCouponRate(0);
      setCouponMin(0);
      setCoupon(null);
    }
  }, [coupon, couponMin, promoSubtotal]);

  // Server'a yaz (debounced) — admin paneli buradan izler.
  // Boş sepet yalnızca daha önce sync edildiyse gönderilir (satır temizliği).
  useEffect(() => {
    if (!initialized.current || !restored.current) return;
    if (items.length === 0 && !everSynced.current) return;
    const ownerKey = userEmail ?? guestId ?? "";
    if (!ownerKey) return;
    const t = setTimeout(() => {
      if (items.length > 0) everSynced.current = true;
      apiPut(
        "/api/cart",
        "cart-sync",
        {
          ownerKey,
          items: items.map((i) => ({ slug: i.slug, name: i.name, price: i.price, quantity: i.quantity, ...(i.image ? { image: i.image } : {}), ...(i.variant ? { variant: i.variant } : {}) })),
          coupon,
        }
      );
    }, 1500);
    return () => clearTimeout(t);
  }, [items, coupon, userEmail, guestId]);

  const addItem = useCallback((item: CartItem) => {
    const qty = Math.max(1, Math.min(99, Math.floor(item.quantity) || 1));
    const key = cartLineKey(item);
    const variant = typeof item.variant === "string" && item.variant ? item.variant.slice(0, 40) : undefined;
    setItems((prev) => {
      const existing = prev.find((p) => cartLineKey(p) === key);
      if (existing) {
        return prev.map((p) =>
          cartLineKey(p) === key ? { ...p, quantity: Math.min(99, p.quantity + qty) } : p
        );
      }
      return [...prev, { ...item, quantity: qty, variant }];
    });
  }, []);

  const removeItem = useCallback((slug: string, variant?: string) => {
    const key = cartLineKey({ slug, variant });
    setItems((prev) => prev.filter((p) => cartLineKey(p) !== key));
  }, []);

  const updateQuantity = useCallback((slug: string, quantity: number, variant?: string) => {
    const qty = Math.floor(quantity);
    const key = cartLineKey({ slug, variant });
    setItems((prev) =>
      qty <= 0
        ? prev.filter((p) => cartLineKey(p) !== key)
        : prev.map((p) => (cartLineKey(p) === key ? { ...p, quantity: Math.min(99, qty) } : p))
    );
  }, []);

  const total = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  // Önce 4 Al 3 Öde (en ucuz bedava), sonra kupon % kalan tutara uygulanır
  const bundle = useMemo(
    () => calcBundle(items.map((i) => ({ slug: i.slug, quantity: i.quantity }))),
    [items]
  );
  const promoDiscount = bundle.discount;
  const discount = useMemo(
    () => Math.round((total - promoDiscount) * couponRate * 100) / 100,
    [total, promoDiscount, couponRate]
  );
  const totalAfterDiscount = useMemo(
    () => Math.round((total - promoDiscount - discount) * 100) / 100,
    [total, promoDiscount, discount]
  );
  const threshold = shipCfg?.threshold ?? FREE_SHIPPING_THRESHOLD;
  const fee = shipCfg?.fee ?? STANDARD_SHIPPING_FEE;
  const freeShip = totalAfterDiscount <= 0 || totalAfterDiscount >= threshold;
  // Tek tip kargo — ekspres farkı yok, eşik altı sabit ücret.
  const shipping = totalAfterDiscount <= 0 ? 0 : Math.round((freeShip ? 0 : fee) * 100) / 100;
  const grandTotal = useMemo(
    () => Math.round((totalAfterDiscount + shipping) * 100) / 100,
    [totalAfterDiscount, shipping]
  );

  const applyCoupon = useCallback(
    async (code: string) => {
      const upper = code.trim().toUpperCase();
      if (!upper) return { ok: false, message: "Kod yazın" };
      const v = await validateCouponOnServer(upper, promoSubtotal);
      if (!v.ok) return { ok: false, message: v.error };
      validatedCoupon.current = upper;
      setCouponRate(v.rate);
      setCouponMin(v.minSubtotal);
      setCoupon(upper);
      return { ok: true, message: `${upper} uygulandı` };
    },
    [promoSubtotal, validateCouponOnServer]
  );
  const removeCoupon = useCallback(() => {
    validatedCoupon.current = null;
    setCouponRate(0);
    setCouponMin(0);
    setCoupon(null);
  }, []);
  const clear = useCallback(() => {
    validatedCoupon.current = null;
    setCouponRate(0);
    setCouponMin(0);
    setItems([]);
    setCoupon(null);
  }, []);

  const value = useMemo(
    () => ({ items, total, discount, promoDiscount, bundle, coupon, totalAfterDiscount, shipping, freeShip, freeShipThreshold: threshold, standardFee: fee, grandTotal, count, addItem, removeItem, updateQuantity, clear, applyCoupon, removeCoupon }),
    [items, total, discount, promoDiscount, bundle, coupon, totalAfterDiscount, shipping, freeShip, threshold, fee, grandTotal, count, addItem, removeItem, updateQuantity, clear, applyCoupon, removeCoupon]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
